import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import GradientIcon from 'components/ui/GradientIcon';
import ContactMethodModal from 'components/modals/ContactMethodModal';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import ClassManagementForm from 'components/modals/ClassManagementForm';

const PricingSection = () => {
  const [pages, setPages] = useState(5);
  const [selectedPlan, setSelectedPlan] = useState('page');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showClassManagementForm, setShowClassManagementForm] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState(null);

  const handleContactMethod = (method) => {
    const phoneNumber = '254793863320';
    let message = '';

    if (selectedPlanForModal === 'page') {
      message = encodeURIComponent(`Hi! I want to order ${pages} pages at $8/page (Total: $${calculateTotal()}). Can you help me get started?`);
    } else {
      message = encodeURIComponent('Hi! I\'m interested in the Full Course package for $199. Can you provide more details?');
    }

    if (method === 'whatsapp') {
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    } else if (method === 'discord') {
      // Handle Discord integration
      console.log('Discord integration for pricing');
    }
  };

  const pricingPlans = [
    {
      id: 'page',
      title: 'Per Page',
      price: 8,
      unit: 'per page',
      description: 'Perfect for individual assignments and essays',
      features: [
        'High-quality academic writing',
        'Plagiarism-free guarantee',
        'Free revisions',
        '24/7 support',
        'On-time delivery',
        'Proper citations included'
      ],
      popular: false,
      color: 'primary'
    },
    {
      id: 'course',
      title: 'Full Course',
      price: 199,
      unit: 'per course',
      description: 'Complete course assistance for entire semester',
      features: [
        'All assignments for one course',
        'Weekly check-ins with expert',
        'Priority support',
        'Grade improvement guarantee',
        'Exam preparation help',
        'Discussion board participation',
        'Final project assistance',
        'Unlimited revisions'
      ],
      popular: true,
      color: 'secondary',
      savings: 'Save up to 40%'
    }
  ];

  const competitorComparison = [
    { feature: 'Upfront Pricing', us: true, competitor: false },
    { feature: 'No Consultation Required', us: true, competitor: false },
    { feature: 'Instant WhatsApp Support', us: true, competitor: false },
    { feature: 'Money-Back Guarantee', us: true, competitor: true },
    { feature: 'Plagiarism Report', us: true, competitor: true },
    { feature: 'Average Cost per Page', us: '$8', competitor: '$25-45' }
  ];

  const calculateTotal = () => {
    return pages * 8;
  };

  const handleOrderNow = (planType) => {
    if (planType === 'page') {
      setShowAssignmentForm(true);
    } else if (planType === 'course') {
      setShowClassManagementForm(true);
    }
  };

  return (
    <section id="pricing" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            <span className="text-gradient">Transparent Pricing</span> That Makes Sense
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            No hidden fees, no consultation required, no surprises. 
            See exactly what you'll pay and get started immediately.
          </p>
        </motion.div>

        {/* Interactive Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-md mx-auto mb-16 glass-card p-6"
        >
          <h3 className="text-xl font-semibold text-text-primary text-center mb-4">
            Quick Price Calculator
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm font-medium mb-2">
                Number of Pages
              </label>
              <input
                type="range"
                min="1"
                max="50"
                value={pages}
                onChange={(e) => setPages(parseInt(e.target.value))}
                className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-text-muted mt-1">
                <span>1</span>
                <span className="font-semibold text-primary">{pages} pages</span>
                <span>50</span>
              </div>
            </div>
            <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
              <div className="text-3xl font-bold text-primary">
                ${calculateTotal()}
              </div>
              <div className="text-text-secondary text-sm">
                {pages} pages × $8 per page
              </div>
            </div>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" style={{padding: '2rem 1rem'}}>
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative glass-card p-8 hover:transform hover:-translate-y-2 transition-all duration-250 ${
                plan.popular ? 'border-2 border-secondary' : ''
              }`}
              style={{overflow: 'visible', position: 'relative'}}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div 
                  className="absolute left-1/2 transform -translate-x-1/2 text-center"
                  style={{
                    top: '-1rem',
                    zIndex: 30,
                    position: 'absolute'
                  }}
                >
                  <div className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-semibold animate-pulse shadow-lg whitespace-nowrap">
                    Most Popular
                  </div>
                </div>
              )}

              {/* Savings Badge */}
              {plan.savings && (
                <div 
                  className="absolute transform"
                  style={{
                    top: '-0.5rem',
                    right: '-0.5rem',
                    zIndex: 30,
                    position: 'absolute'
                  }}
                >
                  <div className="bg-accent text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg whitespace-nowrap">
                    {plan.savings}
                  </div>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-text-primary mb-2">
                  {plan.title}
                </h3>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-gradient">
                    ${plan.price}
                  </span>
                  <span className="text-text-secondary ml-2">
                    {plan.unit}
                  </span>
                </div>
                <p className="text-text-secondary">
                  {plan.description}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary text-sm sm:text-base leading-relaxed break-words">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleOrderNow(plan.id)}
                className={`w-full py-4 px-6 rounded-lg font-semibold transition-all duration-250 ease-smooth ${
                  plan.popular
                    ? 'bg-secondary hover:bg-secondary-600 text-white' :'bg-primary hover:bg-primary-700 text-white'
                }`}
              >
                Order Now - {plan.title}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Competitor Comparison */}
        {/*
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            Why We're Different from <span className="text-error">Expensive Competitors</span>
          </h3>

          <div className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface">
                  <tr>
                    <th className="px-6 py-4 text-left text-text-primary font-semibold">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-text-primary font-semibold">
                      <div className="flex items-center justify-center space-x-2">
                        <GradientIcon name="GraduationCap" size={20} gradient="primary" />
                        <span>SOMO</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-text-primary font-semibold">
                      Other Services
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {competitorComparison.map((item, index) => (
                    <tr key={index} className="border-t border-border">
                      <td className="px-6 py-4 text-text-secondary">
                        {item.feature}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof item.us === 'boolean' ? (
                          item.us ? (
                            <Icon name="Check" size={20} className="text-success mx-auto" />
                          ) : (
                            <Icon name="X" size={20} className="text-error mx-auto" />
                          )
                        ) : (
                          <span className="text-success font-semibold">{item.us}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof item.competitor === 'boolean' ? (
                          item.competitor ? (
                            <Icon name="Check" size={20} className="text-success mx-auto" />
                          ) : (
                            <Icon name="X" size={20} className="text-error mx-auto" />
                          )
                        ) : (
                          <span className="text-error font-semibold">{item.competitor}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
        */}

        {/* No Hidden Fees Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16 p-8 bg-gradient-to-r from-success/10 to-accent/10 rounded-2xl border border-success/20"
        >
          <GradientIcon name="Shield" size={48} gradient="success" containerClassName="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            No Hidden Fees Guarantee
          </h3>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            What you see is what you pay. No consultation fees, no processing charges, 
            no surprise costs. Just transparent, honest pricing that respects your budget.
          </p>
        </motion.div>

        {/* Ready to Order CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-2xl border border-primary/20"
        >
          <GradientIcon name="CreditCard" size={48} gradient="primary" containerClassName="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Ready to Get Started?
          </h3>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-6">
            Join thousands of satisfied students. Get your assignment done by experts 
            with transparent pricing and guaranteed quality.
          </p>
          <button
            onClick={() => setShowAssignmentForm(true)}
            className="btn-primary text-lg px-8 py-4 inline-flex items-center space-x-2"
          >
            <GradientIcon name="MessageCircle" size={24} gradient="secondary" />
            <span>Start Your Order</span>
          </button>
        </motion.div>
      </div>

      {/* Assignment Submission Form */}
      <AssignmentSubmissionForm 
        isOpen={showAssignmentForm} 
        onClose={() => setShowAssignmentForm(false)} 
        onSubmit={() => {
          setShowAssignmentForm(false);
          // Handle successful submission
        }} 
      />

      {/* Class Management Form */}
      <ClassManagementForm 
        isOpen={showClassManagementForm} 
        onClose={() => setShowClassManagementForm(false)} 
        onSubmit={() => {
          setShowClassManagementForm(false);
          // Handle successful submission
        }} 
      />

      {/* Contact Method Modal */}
      <ContactMethodModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
        onSelectMethod={handleContactMethod}
        title={selectedPlanForModal === 'course' ? 'Get Full Course Details' : 'Start Your Assignment'}
      />
    </section>
  );
};

export default PricingSection;
